<template>
    <page-layout
        :readonly="isBriefAccepted"
        :hide-footer="isBriefAccepted"
        :is-preview="isPreview"
    >
        <template slot="header">
            <step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :title="pageTitle"
                :use-exit-modal="false"
                :is-loading="isLoading"
                :is-preview="isPreview"
                :skip-steps="isBriefAccepted"
            />
        </template>
        <template slot="default">
            <g-loader v-if="isLoading" />
            <template v-else>
                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                <step-error v-if="step && step.error" class="margin-bottom-normal" :step="step" />
                <g-typography tag="p" :value="description" class="break-new-line" />
                <g-typography v-if="requiredBriefFields.length > 0" tag="h3" fontWeight="semibold" value="Required" />
                <div v-for="field in requiredBriefFields" :key="field.id">
                    <custom-field-input :field="field.field" :disabled="!isBriefEditable" @input-value="applyValue" />
                </div>
                <br><br>
                <g-typography v-if="optionalBriefFields.length > 0" tag="h3" fontWeight="semibold" value="Optional" />
                <div v-for="field in optionalBriefFields" :key="field.id">
                    <custom-field-input :field="field.field" :disabled="!isBriefEditable" @input-value="applyValue" />
                </div>
            </template>
        </template>
        <template slot="footer">
            <step-footer
                v-if="isBriefEditable"
                :is-next-disabled="isNextDisabled"
                :step="step"
                :loading="isCompletingBrief"
                :use-exit-modal="false"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :is-preview="isPreview"
                @go-next-step="goToNextStep"
                @go-previous-step="goPreviousStep"
            />
        </template>
    </page-layout>
</template>

<script>
    import PageLayout from '../../layouts/PageLayout';
    import {get} from 'lodash';
    import StepError from './partials/StepError';
    import StepHeader from './partials/StepHeader';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import StepFooter from './partials/StepFooter';
    import {getCustomizableText, getCustomizableTitle} from '../../../helpers/activations/default-texts';
    import CustomFieldInput from './partials/CustomFieldInput.vue';
    import ErrorMessageCard from './partials/ErrorMessageCard';

    export default {
        name:       'ActivationBriefPersonalInfoView',
        components: {
            StepFooter,
            StepHeader,
            StepError,
            PageLayout,
            CustomFieldInput,
            ErrorMessageCard,
        },
        mixins: [
            ActivationBriefMixin,
        ],
        props: {
            brief: {
                type:    Object,
                default: null,
            },
            briefFields: {
                type:    Array,
                default: () => [],
            },
            step: {
                type:    Object,
                default: null,
            },
            isLoading: {
                type:    Boolean,
                default: false,
            },
            isCompletingBrief: {
                type:    Boolean,
                default: false,
            },
            errors: {
                type:    Object,
                default: () => ({}),
            },
            errorMessage: {
                type:    String,
                default: '',
            },
        },
        data() {
            return {
                customFields: {},
            };
        },
        computed: {
            requiredBriefFields() {
                return this.briefFields.filter(x => x.is_required);
            },
            optionalBriefFields() {
                return this.briefFields.filter(x => !x.is_required);
            },
            pageTitle() {
                return getCustomizableTitle(this.brief, 'additional_information_title');
            },
            description() {
                return getCustomizableText(this.brief, 'additional_information_description');
            },
            stepNumber() {
                return get(this, 'step.meta.stepNumber', 0);
            },
            totalSteps() {
                return get(this, 'step.meta.totalSteps', 0);
            },
            isCompleted() {
                return get(this, 'step.is_complete', false);
            },
            isNextDisabled() {
                const requiredIds = this.requiredBriefFields.map(field => field.field_id);

                const neededKeysInCustomFieldsObject = requiredIds.map(id => `field_${id}`);
                return !neededKeysInCustomFieldsObject.every(key => this.customFields.hasOwnProperty(key));
            },
        },
        methods: {
            applyValue(data) {
                this.$set(this.customFields, `field_${data.field_id}`, data.value);
            },
            goToNextStep() {
                this.$emit('go-next-step', this.step, (this.customFields && this.customFields !== {}) ? this.customFields : null);
            },
            goPreviousStep() {
                this.$emit('go-previous-step', this.step, (this.customFields && this.customFields !== {}) ? this.customFields : null);
            },
        },
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }
    .margin-bottom-large {
        margin-bottom: $g-spacing-5;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>
